import type { ConfigProps, MenuLocations } from './configTypes';
import { fetchPost, type PostNode } from './api/queries/fetchPost';
import fetchMenusFromApi from './api/queries/fetchMenus';
import fetchEmbeddedPosts from './api/queries/fetchEmbeddedPosts';
import fetchSingleEmbeddedPost from './api/queries/fetchSingleEmbeddedPost';
import fetchPostsByCategory, {
  type PostsByCategoryResult,
} from './api/queries/fetchPostsByCategory';
import fetchPrivatePost, {
  type PrivatePostNode,
} from './api/queries/fetchPrivatePost';
import fetchPostPreview, {
  type PostPreviewNode,
} from './api/queries/fetchPostPreview';
import type { TransformedItemPost } from './api/types';
import fetchSitemapFromApi, {
  type FetchSitemapFromApiParams,
} from './api/queries/fetchSitemapFromApi';
import type { GroupedSitemapResponse, SitemapArticleObject } from './api/types';

/**
 * WordPress API Client — unified interface for all WordPress GraphQL queries.
 * Migrated from se-supersonic-middleware as part of the middleware decommission (EDUN-9322).
 */
export class WordpressApiClient {
  /**
   * Fetches a single post by URI, including content enrichment (embedded posts, rankings, forms, etc.).
   */
  fetchPost(config: ConfigProps, path: string): Promise<PostNode | null> {
    return fetchPost(config, path);
  }

  /**
   * Fetches multiple embedded posts by numeric IDs (database IDs).
   */
  fetchEmbeddedPosts(
    ids: string[],
    config: ConfigProps,
  ): Promise<Record<string, TransformedItemPost> | null> {
    return fetchEmbeddedPosts(ids, config);
  }

  /**
   * Fetches a single embedded post by URI (or slug).
   */
  fetchEmbeddedPost(
    uri: string,
    postTypeGql: string,
    cmsUrl: string,
    fallbackPostImage?: string,
  ): Promise<Record<string, TransformedItemPost> | null> {
    return fetchSingleEmbeddedPost(uri, postTypeGql, cmsUrl, fallbackPostImage);
  }

  /**
   * Fetches menus by location from the WordPress GraphQL API.
   */
  fetchMenus(config: ConfigProps, menuLocations?: MenuLocations) {
    return fetchMenusFromApi(config, menuLocations);
  }

  /**
   * Fetches all posts for a given category slug, including sub-category children,
   * menus, and transformed SEO data.  Used to power hub pages.
   */
  fetchPostsByCategory(
    config: ConfigProps,
    category: string,
    limit: number,
  ): Promise<PostsByCategoryResult | null> {
    return fetchPostsByCategory(config, category, limit);
  }

  /**
   * Fetches a private or draft post by URI using a bearer access token.
   * Used to power the preview and private-page flows.
   */
  fetchPrivatePost(
    uri: string,
    config: ConfigProps,
    accessToken: string,
  ): Promise<PrivatePostNode | null> {
    return fetchPrivatePost(uri, config, accessToken);
  }

  /**
   * Fetches sitemap data from the WordPress API.
   */
  fetchSitemapFromApi(
    params: FetchSitemapFromApiParams,
  ): Promise<GroupedSitemapResponse | SitemapArticleObject[]> {
    return fetchSitemapFromApi(params);
  }

  /**
   * Retrieves a preview of a post from the CMS (Client Side).
   * @param id - The ID of the post to retrieve.
   * @param config - The configuration object for the site.
   * @param accessToken - OAuth access token for authentication.
   * @returns A promise that resolves to a PostPreviewNode object.
   */
  fetchPostPreview(
    id: number | string | undefined,
    config: ConfigProps,
    accessToken: string,
  ): Promise<PostPreviewNode | null> {
    return fetchPostPreview(id, config, accessToken);
  }
}
